import { useState } from "react";
import { Link } from "react-router-dom";
import { priceItems } from "@/mocks/prices";
import PriceSparkline from "@/components/feature/PriceSparkline";
import {
  directionBadge,
  directionClass,
  formatPercent,
  formatPrice,
  formatSigned,
  trendColor,
} from "@/utils/format";

const TABS = [
  { value: "all", label: "全部" },
  { value: "spot", label: "现货" },
  { value: "futures", label: "期货" },
];

const FEATURED_IDS = ["au9999", "london-gold", "shfe-au", "comex-gold", "gold-td", "comex-gold-cont"];

export default function PriceOverview() {
  const [tab, setTab] = useState("all");

  const list = FEATURED_IDS.map((id) => priceItems.find((item) => item.id === id)).filter(
    (item): item is (typeof priceItems)[number] => Boolean(item)
  );

  const filtered = tab === "all" ? list : list.filter((item) => item.category === tab);

  return (
    <section className="px-4 md:px-6 py-14 md:py-20">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-label text-primary-700 mb-3">
            <span className="w-6 h-px bg-primary-500" />
            实时快览
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-2">黄金行情快览</h2>
          <p className="text-sm text-foreground-500 max-w-2xl leading-relaxed">
            汇总黄金现货与期货的核心品种价格与涨跌动态，所有数据均为本站演示数据。
          </p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 self-start md:self-auto">
          {TABS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setTab(item.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors ${
                tab === item.value
                  ? "bg-accent-950 text-foreground-50"
                  : "text-foreground-600 hover:text-accent-950"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((item) => (
            <Link
              key={item.id}
              to={`/detail/price/${item.id}`}
              data-product-shop
              className="group rounded-lg border border-background-200 bg-background-50 p-5 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h3 className="font-heading font-bold text-base text-foreground-950 truncate">{item.name}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{item.symbol} · {item.unit}</p>
                </div>
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-label border whitespace-nowrap ${directionBadge(
                    item.changePercent
                  )}`}
                >
                  {item.category === "spot" ? "现货" : "期货"}
                </span>
              </div>

              <div className="flex items-end justify-between gap-3 mb-3">
                <p className="font-heading font-bold text-2xl text-foreground-950">{formatPrice(item.price)}</p>
                <p className={`text-sm font-medium ${directionClass(item.changePercent)}`}>
                  {formatSigned(item.change)}
                </p>
              </div>

              <p className={`flex items-center gap-1 text-sm mb-3 ${directionClass(item.changePercent)}`}>
                <i className={item.changePercent >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                {formatPercent(item.changePercent)}
                <span className="text-foreground-400 ml-1">较昨收</span>
              </p>

              <PriceSparkline data={item.trend} color={trendColor(item.changePercent)} height={52} />

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200 text-xs text-foreground-400">
                <span>高 {formatPrice(item.high)} / 低 {formatPrice(item.low)}</span>
                <span className="group-hover:text-primary-600 transition-colors flex items-center gap-1">
                  详情
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-300" />
          <p className="mt-3 text-sm text-foreground-500">当前分类暂无演示行情数据</p>
          <button
            type="button"
            onClick={() => setTab("all")}
            className="mt-4 px-5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-accent-950 text-sm font-medium whitespace-nowrap transition-colors"
          >
            重置筛选
          </button>
        </div>
      )}
    </section>
  );
}